import { useDrawerState } from "@/store/drawer-state";
import styled from "@emotion/styled";
import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";

function Drawer({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) {
  return <DrawerPrimitive.Root data-slot="drawer" {...props} />;
}

function DrawerTrigger({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Trigger>) {
  return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />;
}

function DrawerPortal({
  children,
  container,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Portal> & {
  container?: Element | null;
}) {
  if (!container) {
    return <>{children}</>;
  }
  return (
    <DrawerPrimitive.Portal container={container} {...props}>
      {children}
    </DrawerPrimitive.Portal>
  );
}

function DrawerClose({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Close>) {
  return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />;
}

const StyledOverlay = styled.div`
  /* Ensure clicks are registered */
  pointer-events: auto;
  touch-action: none;
`;

const StyledContent = styled(DrawerPrimitive.Content)`
  background: #fff;
  display: flex;
  flex-direction: column;
  height: auto;
`;

function DrawerContent({
  className,
  children,
  container,
  showOverlay = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Content> & {
  container?: Element | null;
  showOverlay?: boolean;
}) {
  const { closeDrawer, isDrawerOpen } = useDrawerState();
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    closeDrawer();
  };

  return (
    <DrawerPortal container={container}>
      {showOverlay && isDrawerOpen && (
        <StyledOverlay
          onClick={handleOverlayClick}
          data-vaul-overlay
          data-state={isDrawerOpen ? "open" : "closed"}
        />
      )}
      <StyledContent {...props} className={className}>
        {children}
      </StyledContent>
    </DrawerPortal>
  );
}

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
`;
function DrawerHeader({ style, ...props }: React.ComponentProps<"div">) {
  return <StyledHeader {...props} />;
}

const StyledFooter = styled.div`
  margin-top: auto;
  padding: 16px;
`;
function DrawerFooter({ style, ...props }: React.ComponentProps<"div">) {
  return <StyledFooter {...props} />;
}

const StyledTitle = styled(DrawerPrimitive.Title)`
  font-weight: 600;
  font-size: 1.25rem;
`;
function DrawerTitle({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Title>) {
  return <StyledTitle {...props} />;
}

const StyledDescription = styled(DrawerPrimitive.Description)`
  font-size: 14px;
  color: #6b7280;
`;
function DrawerDescription({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Description>) {
  return <StyledDescription data-slot="drawer-description" {...props} />;
}

function DrawerHandle({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Handle>) {
  return <DrawerPrimitive.Handle {...props}></DrawerPrimitive.Handle>;
}

// Export the original for compatibility, but we use StyledOverlay internally
const DrawerOverlay = DrawerPrimitive.Overlay;

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHandle,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
};
